import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { pathOr } from 'ramda'
import { compose, defaultProps, withHandlers, withProps, withState } from 'recompose'

import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralDisclosureRequired from 'components/HOCs/WithReferralDisclosureRequired'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import initialStateQuery from 'src/pages/StartInvesting/gql/initialState.query.gql'
import updateRecurringInvestmentMutation from 'src/pages/StartInvesting/gql/updateRecurringInvestment.mutation.gql'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withOptimizely } from 'utils/Optimizely'

import { InnerProps, InvestmentMode } from '../../types'
import { readUserSettings, saveUserSettings } from '../../utilities'

export const RECURRING_AMOUNTS_LATER = ['5', '10', '25', '50', '105']
export const DEFAULT_RECURRING = '5'

export const investmentAmount = (props: any) => {
  return props.recurringAmount || DEFAULT_RECURRING
}

const enhance = compose<InnerProps, {}>(
  defaultProps({
    loading: true,
    investmentMode: InvestmentMode.Recurring,
  }),
  withReferralDisclosureRequired(),
  withFlowRouter,
  withRouter,
  withHeroText,
  withPartnerProps,
  withRemoteStrings,
  withOptimizely,
  withState(
    'recurringAmount',
    'setRecurringAmount',
    readUserSettings('recurringAmount', DEFAULT_RECURRING),
  ),
  withState('confirmModalOpen', 'setConfirmModalOpen', false),
  withState('noFundingSourceModalOpen', 'setNoFundingSourceModalOpen', false),
  withState('submitting', 'setSubmitting', false),
  graphql(updateRecurringInvestmentMutation, { name: 'updateRecurringInvestment' }),
  graphql(initialStateQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data }) => {
      return {
        loading: data.loading,
        fundingSource: pathOr(null, ['fundingSourceInfo'], data),
        laterAccountId: pathOr(null, ['laterAccounts', 0, 'accountId'], data),
        recurringAmount: readUserSettings(
          'recurringAmount',
          pathOr(DEFAULT_RECURRING, ['recurringInvestmentSettings', 'amount'], data).toString(),
        ),
        investmentMode: InvestmentMode.Recurring,
      }
    },
  }),
  withProps(({ getRemoteString, heroText }) => {
    return {
      header: getRemoteString('later_recurring_investment_header', heroText.header),
      text: getRemoteString('later_recurring_investment_text', heroText.text),
    }
  }),
  withHandlers<InnerProps, {}>({
    handleCTA: ({
      setConfirmModalOpen,
      setNoFundingSourceModalOpen,
      fundingSource,
      flow,
      partner,
    }) => () => {
      if (fundingSource) {
        setConfirmModalOpen(true)
      } else {
        setNoFundingSourceModalOpen(true)
      }
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(
            TRACKER_IDS.START_INVESTING.LATER_ONBOARDING_RECURRING_START_INVESTING,
          ),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })
    },
    handleSkip: ({ route, flow, partner }) => () => {
      saveUserSettings()

      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.LATER_ONBOARDING_RECURRING_SKIP),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })

      route({ from: FlowRoute.RecurringInvestment, action: 'one-time' })
    },
    handleLinkAccount: ({ route: route }) => () => {
      route({ from: FlowRoute.RecurringInvestment, action: 'link-account' })
    },
    handleRecurringAmountChange: ({
      setRecurringAmount,
      oneTimeAmount,
      investmentMode,
      flow,
      partner,
    }) => ({ currentTarget }) => {
      saveUserSettings({
        recurringAmount: currentTarget.dataset.value,
        oneTimeAmount,
        investmentMode,
      })
      setRecurringAmount(currentTarget.dataset.value)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.CHANGE_RECURRING_AMOUNT),
          ...getDefaultEventData('Button Tapped'),
          cta_title: currentTarget.dataset.value,
        },
        flow,
        partner,
      })
    },
    handleModalClosed: ({ setConfirmModalOpen }) => () => {
      setConfirmModalOpen(false)
    },
    handleModalConfirmed: ({
      route: route,
      referralDisclosureRequired,
      updateRecurringInvestment,
      investmentMode,
      recurringAmount,
      oneTimeAmount,
      setConfirmModalOpen,
      setSubmitting,
      laterAccountId,
      trackOptimizely,
    }) => () => {
      setConfirmModalOpen(false)
      setSubmitting(true)

      const input = {
        amount: parseFloat(investmentAmount({ investmentMode, recurringAmount, oneTimeAmount })),
        day: new Date().getDay() + 1,
        frequency: 'weekly',
      }
      const variables = { input: { ...input, investmentAccountId: laterAccountId } }

      return updateRecurringInvestment({ variables })
        .then(() => {
          saveUserSettings()
          trackOptimizely('recurring_investment.complete')
          route({
            from: FlowRoute.RecurringInvestment,
            action: 'continue',
            state: { referralDisclosureRequired },
            locationState: { investment: input },
          })
        })
        .catch(err => {
          sendToSentry(err)
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  }),
)

export default enhance
